import {
  ArrowLeftRightIcon,
  FilesIcon,
  LinkIcon,
  MailboxIcon,
  SplitIcon,
  type LucideIcon,
} from "lucide-react";

import {
  type RuleDefinitionSuggestionTypeEnum,
  TriageQueueEnum,
} from "@/__generated__/graphql";
import { iconsByTypename } from "@/objects/@icons";

export { TriageQueueEnum };

export const TRIAGE_KEYS = {
  ACCEPT_META: {
    mac: "meta+enter",
    windows: "ctrl+enter",
    linux: "ctrl+enter",
  },
  /** The payee's others, opened to book like the payment open: L, for "like it". */
  BOOK_LIKE: "l",
  HOLD: "h",
  RECONCILE: "r",
  /** In the suggestions, where the queue's own does not reach: turn the one the keyboard is on down. */
  REJECT: "r",
  /** A document match the keyboard is on: its document, opened to its preview, to match from there. */
  VIEW_DOCUMENT: "v",
  /** In the suggestions: the category of the posting the keyboard is on, changed. */
  CATEGORY: "c",
  /** A suggestion the keyboard is on, moved to another of the person's businesses. */
  MOVE: "m",
  ADD_LINE: "n",
  PREVIEW: "p",
  /** A draft in review: open the chat it waits in, to put it right first. */
  DRAFT_THREAD: "t",
  /** A draft in review, opened: confirm its change where it is, to book what it makes. */
  ACCEPT_DRAFT: "a",
  /** The tags of the payment open, on its row: from the queue or the suggestions alike. */
  TAGS: "x",
  /** A card's own actions, behind its ⋯ - its suggestions worked out again, and the Match card's mailboxes and forwarding address. */
  ACTIONS: "o",
  /** A payment lined up with no document: its mailboxes searched for one, from its empty line. */
  SEARCH_MAILBOXES: "f",
} as const;

/** A kind of suggestion, by the value the schema gives it: each map below names every kind there is. */
export type TriageJobEnum = `${RuleDefinitionSuggestionTypeEnum}`;

/** What each queue is called where the queue is filtered by it. */
export const QUEUE_LABELS: Record<TriageQueueEnum, string> = {
  [TriageQueueEnum.MissingDocument]: "Missing a document",
  [TriageQueueEnum.Moved]: "For another business",
  [TriageQueueEnum.AiSuggested]: "Suggested by AI",
  [TriageQueueEnum.NeedsMatch]: "Document match suggested",
  [TriageQueueEnum.Emailed]: "From email",
  [TriageQueueEnum.Duplicates]: "Possible duplicates",
  [TriageQueueEnum.NeedsCategory]: "No category",
  [TriageQueueEnum.NeedsJe]: "Categorized, not booked",
  [TriageQueueEnum.Ready]: "Ready to book",
  [TriageQueueEnum.NoSuggestions]: "No suggestions",
  [TriageQueueEnum.Processing]: "AI at work",
  [TriageQueueEnum.Booked]: "Booked",
  [TriageQueueEnum.OnHold]: "On hold",
};

export const JOB_LABELS: Record<TriageJobEnum, string> = {
  CATEGORIZE: "Categorize",
  MATCH_DOCUMENT: "Match Document",
  DUPLICATE: "Duplicate",
  SPLIT: "Split",
  TRANSFER: "Transfer",
  PAIR: "Pair",
};

/** What a way to book a payment does, in a word: the queue's row lists them so. */
export const JOB_VERBS: Record<TriageJobEnum, string> = {
  CATEGORIZE: "Post",
  MATCH_DOCUMENT: "Match",
  DUPLICATE: "Duplicate",
  SPLIT: "Split",
  TRANSFER: "Transfer",
  PAIR: "Pair",
};

/**
 * What a suggestion of each kind leads its card with: the record it brings in
 * where it brings one - a document - and otherwise what it does.
 */
export const JOB_ICONS: Record<TriageJobEnum, LucideIcon> = {
  // Posted to an account: what goes in, as a letter into its box.
  CATEGORIZE: MailboxIcon,
  MATCH_DOCUMENT: iconsByTypename.DocumentObject,
  DUPLICATE: FilesIcon,
  SPLIT: SplitIcon,
  TRANSFER: ArrowLeftRightIcon,
  PAIR: LinkIcon,
};

/** What a suggestion of each kind would do to the payment, to finish "would …". */
export const JOB_ACTIONS: Record<TriageJobEnum, string> = {
  CATEGORIZE: "categorize it",
  MATCH_DOCUMENT: "match it to a document",
  DUPLICATE: "mark it as a duplicate",
  SPLIT: "split it",
  TRANSFER: "book it as a transfer",
  PAIR: "pair it with another transfer",
};
